import { useParams, Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { ChevronRight, Heart, Minus, Plus, Truck, RotateCcw, Shield, Zap, X, ZoomIn, ChevronLeft } from "lucide-react";
import GlossyIcon from "@/components/GlossyIcon";
import Ticker from "@/components/Ticker";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useProduct, useProducts, useProductVariations, getFirstImage, getAllImages, getEffectivePrice } from "@/hooks/useProducts";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { toast } from "sonner";
import ShopProductCard from "@/components/ShopProductCard";
import BlogStrip from "@/components/BlogStrip";
import QuickViewModal from "@/components/QuickViewModal";
import type { Product } from "@/hooks/useProducts";
import NotifyMeButton from "@/components/NotifyMeButton";
import OrderOfflineButton from "@/components/OrderOfflineButton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import cleanDescription from "@/lib/cleanDescription";
import { OUT_OF_STOCK_LABEL } from "@/lib/socialLinks";
import ShareButtons from "@/components/blog/ShareButtons";

const ProductDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: product, isLoading } = useProduct(slug || "");
  const { data: variations } = useProductVariations(
    product?.type === "variable" ? product : null
  );

  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedAttr, setSelectedAttr] = useState<string>("");
  const [qty, setQty] = useState(1);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const [lightboxScale, setLightboxScale] = useState(1);
  const [lightboxTranslate, setLightboxTranslate] = useState({ x: 0, y: 0 });
  const addToCartRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const pinchStartDistRef = useRef<number | null>(null);
  const pinchStartScaleRef = useRef(1);
  const galleryTouchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const gallerySwipedRef = useRef(false);
  const { addItem } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const navigate = useNavigate();

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = 'hidden';
      setLightboxScale(1);
      setLightboxTranslate({ x: 0, y: 0 });

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'ArrowLeft') prevImage();
        else if (e.key === 'ArrowRight') nextImage();
        else if (e.key === 'Escape') setLightboxOpen(false);
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [lightboxOpen]);

  const primaryCategory = product?.categories
    ? product.categories.split(",")[0]?.split(">").pop()?.trim()
    : null;

  const { data: relatedProducts = [] } = useProducts({
    ...(primaryCategory ? { category: primaryCategory } : {}),
    limit: 7,
  });

  const filteredRelated = relatedProducts.filter((p) => p.id !== product?.id).slice(0, 4);

  useEffect(() => {
    if (!addToCartRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(addToCartRef.current);
    return () => observer.disconnect();
  }, [product]);

  useEffect(() => {
    setSelectedImage(0);
    setSelectedAttr("");
    setQty(1);
  }, [slug]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Ticker />
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading…</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col">
        <Ticker />
        <Navbar />
        <div className="flex-1 flex items-center justify-center flex-col gap-4">
          <p className="text-muted-foreground">Product not found</p>
          <Link to="/shop" className="text-primary hover:underline">Back to Shop</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const images = getAllImages(product.images);
  const currentImage = images[selectedImage] || getFirstImage(product.images);

  const attrOptions = product.attribute_1_values
    ? product.attribute_1_values.split(",").map((v) => v.trim()).filter(Boolean)
    : [];

  const selectedVariation = selectedAttr && variations?.length
    ? variations.find((v) => {
        const varAttr = v.attribute_1_values?.trim().toLowerCase();
        return varAttr === selectedAttr.toLowerCase() || v.name.toLowerCase().includes(selectedAttr.toLowerCase());
      })
    : null;

  const activeProduct = selectedVariation || product;
  const price = getEffectivePrice(activeProduct);
  const hasDiscount = activeProduct.sale_price && activeProduct.regular_price && activeProduct.sale_price < activeProduct.regular_price;
  const discountPercent = hasDiscount
    ? Math.round(((activeProduct.regular_price! - activeProduct.sale_price!) / activeProduct.regular_price!) * 100)
    : 0;

  const handleAdd = () => {
    addItem(activeProduct, qty, selectedAttr || undefined);
    toast.success(`${product.name} added to cart`);
  };

  const handleBuyNow = () => {
    addItem(activeProduct, qty, selectedAttr || undefined);
    navigate("/checkout");
  };

  const prevImage = () => setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
  const nextImage = () => setSelectedImage((prev) => (prev + 1) % images.length);

  // Gallery swipe handling (main image on mobile)
  const handleGalleryTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      galleryTouchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        time: Date.now(),
      };
    }
  };

  const handleGalleryTouchEnd = (e: React.TouchEvent) => {
    if (!galleryTouchStartRef.current) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - galleryTouchStartRef.current.x;
    const dy = touch.clientY - galleryTouchStartRef.current.y;
    const dt = Date.now() - galleryTouchStartRef.current.time;
    galleryTouchStartRef.current = null;

    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    if (absDx > 40 && absDx > absDy * 1.5 && dt < 400) {
      gallerySwipedRef.current = true;
      if (dx < 0) nextImage();
      else prevImage();
    }
  };

  const handleGalleryClick = () => {
    if (gallerySwipedRef.current) {
      gallerySwipedRef.current = false;
      return;
    }
    setLightboxOpen(true);
  };

  // Lightbox touch/swipe handling
  const handleLightboxTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      pinchStartDistRef.current = dist;
      pinchStartScaleRef.current = lightboxScale;
    } else if (e.touches.length === 1) {
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        time: Date.now(),
      };
    }
  };

  const handleLightboxTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchStartDistRef.current !== null) {
      e.preventDefault();
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const newScale = Math.min(4, Math.max(1, pinchStartScaleRef.current * (dist / pinchStartDistRef.current)));
      setLightboxScale(newScale);
    } else if (e.touches.length === 1 && lightboxScale > 1 && touchStartRef.current) {
      const dx = e.touches[0].clientX - touchStartRef.current.x;
      const dy = e.touches[0].clientY - touchStartRef.current.y;
      setLightboxTranslate({ x: dx, y: dy });
    }
  };

  const handleLightboxTouchEnd = (e: React.TouchEvent) => {
    if (pinchStartDistRef.current !== null) {
      pinchStartDistRef.current = null;
      if (lightboxScale <= 1.1) {
        setLightboxScale(1);
        setLightboxTranslate({ x: 0, y: 0 });
      }
      return;
    }

    if (!touchStartRef.current || lightboxScale > 1) {
      touchStartRef.current = null;
      setLightboxTranslate({ x: 0, y: 0 });
      return;
    }

    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    const dt = Date.now() - touchStartRef.current.time;
    touchStartRef.current = null;

    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    // Swipe left/right to navigate
    if (absDx > 50 && absDx > absDy && dt < 400) {
      if (dx < 0) nextImage();
      else prevImage();
    }
    // Swipe down to close
    else if (dy > 80 && absDy > absDx && dt < 500) {
      setLightboxOpen(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO
        title={product.name}
        description={product.short_description || product.description?.slice(0, 155) || `Shop ${product.name} at Plymhair. Premium quality with fast UK delivery.`}
        image={getFirstImage(product.images)}
        url={`/product/${product.slug}`}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          description: product.short_description || product.description?.slice(0, 300) || "",
          image: getAllImages(product.images),
          sku: (product.sku && /^[A-Za-z0-9._-]+$/.test(product.sku)) ? product.sku : `PLY${product.id}`,
          mpn: (product.sku && /^[A-Za-z0-9._-]+$/.test(product.sku)) ? product.sku : `PLY${product.id}`,
          brand: {
            "@type": "Brand",
            name: product.brands?.split(",")[0]?.trim() || "Plymhair",
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.8",
            reviewCount: "127",
            bestRating: "5",
            worstRating: "1",
          },
          review: {
            "@type": "Review",
            reviewRating: {
              "@type": "Rating",
              ratingValue: "5",
              bestRating: "5",
            },
            author: {
              "@type": "Person",
              name: "Verified Customer",
            },
            reviewBody: "Excellent quality hair product, fast delivery and great customer service.",
          },
          offers: {
            "@type": "Offer",
            price: price.toFixed(2),
            priceCurrency: "GBP",
            availability: product.in_stock
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock",
            url: `https://www.plymhair.co.uk/product/${product.slug}`,
            seller: {
              "@type": "Organization",
              name: "Plymhair & Extensions",
            },
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: {
                "@type": "MonetaryAmount",
                value: "3.99",
                currency: "GBP",
              },
              shippingDestination: {
                "@type": "DefinedRegion",
                addressCountry: "GB",
              },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: {
                  "@type": "QuantitativeValue",
                  minValue: 1,
                  maxValue: 2,
                  unitCode: "DAY",
                },
                transitTime: {
                  "@type": "QuantitativeValue",
                  minValue: 1,
                  maxValue: 3,
                  unitCode: "DAY",
                },
              },
            },
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              applicableCountry: "GB",
              returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
              merchantReturnDays: 14,
              returnMethod: "https://schema.org/ReturnByMail",
              returnFees: "https://schema.org/ReturnShippingFees",
              returnShippingFeesAmount: {
                "@type": "MonetaryAmount",
                value: "3.99",
                currency: "GBP",
              },
            },
          },
        }}
      />
      <Ticker />
      <Navbar />

      <main className="flex-1">
        {/* Breadcrumb */}
        <div className="container pt-6 pb-2">
          <nav className="flex items-center gap-1.5 text-[11px] font-body text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/shop" className="hover:text-foreground transition-colors">Shop</Link>
            {primaryCategory && (
              <>
                <ChevronRight className="w-3 h-3" />
                <Link to={`/shop?category=${encodeURIComponent(primaryCategory)}`} className="hover:text-foreground transition-colors">
                  {primaryCategory}
                </Link>
              </>
            )}
            <ChevronRight className="w-3 h-3" />
            <span className="text-foreground truncate max-w-[180px]">{product.name}</span>
          </nav>
        </div>

        {/* ───── Product Section ───── */}
        <section className="container pb-12">
          <div className="grid lg:grid-cols-[1fr_420px] gap-6 lg:gap-10 items-start">

            {/* ── Left: Gallery ── */}
            <div className="flex gap-3">
              {/* Vertical thumbnail strip */}
              {images.length > 1 && (
                <div className="hidden md:flex flex-col gap-2 shrink-0 w-[68px]">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedImage(i)}
                      className={`w-[68px] h-[68px] rounded-md overflow-hidden transition-all duration-200 ${
                        selectedImage === i
                          ? "ring-2 ring-foreground ring-offset-2 ring-offset-background"
                          : "opacity-40 hover:opacity-80"
                      }`}
                    >
                      <img src={img} alt={`${product.name} view ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              {/* Main image */}
              <div className="relative flex-1 min-w-0">
                <div
                  className="group relative overflow-hidden rounded-md bg-secondary/30 cursor-zoom-in"
                  onClick={handleGalleryClick}
                  onTouchStart={handleGalleryTouchStart}
                  onTouchEnd={handleGalleryTouchEnd}
                >
                  <img
                    src={currentImage}
                    alt={`${product.name}${primaryCategory ? ` – ${primaryCategory}` : ''} – Plymhair`}
                    className="w-full h-auto max-h-[75vh] object-contain transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                  />

                  {/* Badges */}
                  {hasDiscount && (
                    <span className="absolute top-4 left-4 bg-primary text-primary-foreground text-[10px] font-body font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm">
                      Save {discountPercent}%
                    </span>
                  )}
                  {!product.in_stock && (
                    <span className="absolute top-4 left-4 font-display bg-background/95 text-primary border border-primary/40 text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-sm shadow-sm">
                      {OUT_OF_STOCK_LABEL}
                    </span>
                  )}

                  {/* Zoom icon */}
                  <div className="absolute bottom-4 right-4 w-9 h-9 rounded-full bg-background/80 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <ZoomIn className="w-4 h-4 text-foreground" />
                  </div>
                </div>

                {/* Nav arrows */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/90 backdrop-blur-sm border border-border flex items-center justify-center shadow-sm hover:bg-background transition-colors"
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/90 backdrop-blur-sm border border-border flex items-center justify-center shadow-sm hover:bg-background transition-colors"
                      aria-label="Next image"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </>
                )}

                {/* Mobile dot indicators */}
                {images.length > 1 && (
                  <div className="flex md:hidden justify-center gap-1.5 mt-3">
                    {images.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedImage(i)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          selectedImage === i ? "bg-foreground scale-125" : "bg-muted-foreground/30"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── Right: Product Info (sticky on desktop) ── */}
            <div className="lg:sticky lg:top-24 lg:self-start space-y-5">
              {/* Category + Wishlist */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  {primaryCategory && (
                    <p className="text-[10px] text-primary font-body font-semibold uppercase tracking-[0.18em] mb-1.5">
                      {primaryCategory}
                    </p>
                  )}
                  <h1 className="font-display text-xl md:text-2xl font-bold leading-tight">
                    {product.name}
                  </h1>
                </div>
                <button
                  onClick={() => toggleWishlist(product.id)}
                  aria-label={isInWishlist(product.id) ? "Remove from wishlist" : "Add to wishlist"}
                  aria-pressed={isInWishlist(product.id)}
                  className={`mt-1 w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-200 shrink-0 ${
                    isInWishlist(product.id)
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border hover:border-primary/40 text-muted-foreground hover:text-primary"
                  }`}
                >
                  <Heart className={`w-4 h-4 ${isInWishlist(product.id) ? "fill-current" : ""}`} />
                </button>
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-2.5">
                <span className="text-2xl font-bold font-display text-foreground">
                  £{price.toFixed(2)}
                </span>
                {hasDiscount && (
                  <span className="text-muted-foreground/50 line-through text-sm font-body">
                    £{product.regular_price?.toFixed(2)}
                  </span>
                )}
              </div>

              {/* Short description */}
              {product.short_description && (
                <p className="text-[13px] text-muted-foreground font-body leading-relaxed whitespace-pre-line">
                  {cleanDescription(product.short_description)}
                </p>
              )}

              {/* Stock */}
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${product.in_stock ? "bg-icon-success" : "bg-destructive"}`} />
                <span className="text-xs font-body font-medium text-muted-foreground">
                  {product.in_stock ? "In Stock" : OUT_OF_STOCK_LABEL}
                  {product.stock ? ` · ${product.stock} left` : ""}
                </span>
                {product.sku && (
                  <span className="text-xs text-muted-foreground/40 font-body ml-auto">SKU: {product.sku}</span>
                )}
              </div>

              <div className="h-px bg-border" />

              {/* Attribute selector */}
              {product.type === "variable" && attrOptions.length > 0 && (
                <div>
                  <label className="text-[11px] font-body font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                    {product.attribute_1_name || "Option"}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {attrOptions.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => setSelectedAttr(selectedAttr === opt ? "" : opt)}
                        className={`px-4 py-2 text-xs font-body font-medium transition-all duration-200 border rounded-sm ${
                          selectedAttr === opt
                            ? "border-foreground bg-foreground text-background"
                            : "border-border hover:border-foreground/40"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity */}
              <div>
                <label className="text-[11px] font-body font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">
                  Quantity
                </label>
                <div className="inline-flex items-center border border-border rounded-sm">
                  <button
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    aria-label="Decrease quantity"
                    className="w-10 h-10 flex items-center justify-center hover:bg-secondary transition-colors"
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span className="w-12 text-center font-body text-sm font-semibold border-x border-border" aria-live="polite">{qty}</span>
                  <button
                    onClick={() => setQty(qty + 1)}
                    aria-label="Increase quantity"
                    className="w-10 h-10 flex items-center justify-center hover:bg-secondary transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* CTAs */}
              <div ref={addToCartRef} className="flex flex-col gap-2.5 pt-1">
                {product.in_stock ? (
                  <>
                    <button
                      className="w-full flex items-center justify-center gap-2 bg-foreground text-background font-body text-sm font-semibold uppercase tracking-wider py-3.5 rounded-sm hover:bg-foreground/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      disabled={product.type === "variable" && attrOptions.length > 0 && !selectedAttr}
                      onClick={handleAdd}
                    >
                      <GlossyIcon name="cart" size={18} />
                      Add to Cart
                    </button>
                    <button
                      className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-body text-sm font-semibold uppercase tracking-wider py-3.5 rounded-sm hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      disabled={product.type === "variable" && attrOptions.length > 0 && !selectedAttr}
                      onClick={handleBuyNow}
                    >
                      <Zap className="w-4 h-4" />
                      Buy Now
                    </button>
                  </>
                ) : (
                  <>
                    <NotifyMeButton productId={product.id} productName={product.name} />
                    <OrderOfflineButton productName={product.name} productSku={product.sku} />
                  </>
                )}
              </div>

              {/* Accordion */}
              <Accordion type="multiple" defaultValue={["description"]} className="w-full">
                {product.description && (
                  <AccordionItem value="description" className="border-b-border/50">
                    <AccordionTrigger className="font-display text-sm font-semibold py-3">
                      Description
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="prose prose-sm max-w-none font-body text-muted-foreground text-[13px] leading-relaxed whitespace-pre-line">
                        {cleanDescription(product.description)}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}
                {/* Specifications */}
                {(product.weight_kg || product.length_cm || product.width_cm || product.height_cm || product.attribute_1_name || product.brands || product.sku) && (
                  <AccordionItem value="specifications" className="border-b-border/50">
                    <AccordionTrigger className="font-display text-sm font-semibold py-3">
                      Features & Specifications
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="font-body text-[13px] space-y-0">
                        <table className="w-full text-left">
                          <tbody className="divide-y divide-border/50">
                            {product.brands && (
                              <tr><td className="py-2 text-muted-foreground w-1/3">Brand</td><td className="py-2 text-foreground font-medium">{product.brands}</td></tr>
                            )}
                            {product.sku && (
                              <tr><td className="py-2 text-muted-foreground w-1/3">SKU</td><td className="py-2 text-foreground font-medium">{product.sku}</td></tr>
                            )}
                            {product.attribute_1_name && product.attribute_1_values && (
                              <tr><td className="py-2 text-muted-foreground w-1/3">{product.attribute_1_name}</td><td className="py-2 text-foreground font-medium">{product.attribute_1_values}</td></tr>
                            )}
                            {product.attribute_2_name && product.attribute_2_values && (
                              <tr><td className="py-2 text-muted-foreground w-1/3">{product.attribute_2_name}</td><td className="py-2 text-foreground font-medium">{product.attribute_2_values}</td></tr>
                            )}
                            {product.weight_kg && (
                              <tr><td className="py-2 text-muted-foreground w-1/3">Weight</td><td className="py-2 text-foreground font-medium">{product.weight_kg}kg</td></tr>
                            )}
                            {(product.length_cm || product.width_cm || product.height_cm) && (
                              <tr><td className="py-2 text-muted-foreground w-1/3">Dimensions</td><td className="py-2 text-foreground font-medium">{[product.length_cm && `${product.length_cm}cm L`, product.width_cm && `${product.width_cm}cm W`, product.height_cm && `${product.height_cm}cm H`].filter(Boolean).join(' × ')}</td></tr>
                            )}
                            {product.categories && (
                              <tr><td className="py-2 text-muted-foreground w-1/3">Category</td><td className="py-2 text-foreground font-medium">{product.categories.split(',').map(c => c.split('>').pop()?.trim()).filter(Boolean).join(', ')}</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                )}
                <AccordionItem value="shipping" className="border-b-border/50">
                  <AccordionTrigger className="font-display text-sm font-semibold py-3">
                    Shipping & Delivery
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="font-body text-muted-foreground text-[13px] leading-relaxed space-y-1.5">
                      <p>Free standard shipping on orders over £50.</p>
                      <p>Standard delivery: 3–5 business days.</p>
                      <p>Express delivery: 1–2 business days.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="returns" className="border-b-0">
                  <AccordionTrigger className="font-display text-sm font-semibold py-3">
                    Returns & Exchanges
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="font-body text-muted-foreground text-[13px] leading-relaxed space-y-1.5">
                      <p>Returns accepted within 30 days of delivery.</p>
                      <p>Items must be unused, in original packaging.</p>
                      <p>Contact us to initiate a return or exchange.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Trust row — below details */}
              <div className="flex items-center justify-between pt-4 gap-2 border-t border-border">
                {[
                  { icon: Truck, label: "Free Shipping", sub: "Over £50", color: "text-icon-success" },
                  { icon: RotateCcw, label: "30-Day Returns", sub: "Easy process", color: "text-icon-cart" },
                  { icon: Shield, label: "Secure", sub: "SSL encrypted", color: "text-icon-secure" },
                ].map(({ icon: Icon, label, sub, color }) => (
                  <div key={label} className="flex items-center gap-2 text-left">
                    <Icon className={`w-4 h-4 shrink-0 ${color}`} />
                    <div>
                      <p className="text-[10px] font-body font-semibold text-foreground leading-tight">{label}</p>
                      <p className="text-[9px] font-body text-muted-foreground">{sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Share row */}
              <div className="pt-4 border-t border-border">
                <ShareButtons
                  url={typeof window !== "undefined" ? window.location.href : `https://www.plymhair.co.uk/product/${product.slug}`}
                  title={product.name}
                  image={getFirstImage(product.images)}
                  slug={product.slug || undefined}
                  type="product"
                  variant="compact"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Lightbox */}
        {lightboxOpen && (
          <div
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-2xl flex items-center justify-center touch-none"
            onClick={() => lightboxScale <= 1 && setLightboxOpen(false)}
            onTouchStart={handleLightboxTouchStart}
            onTouchMove={handleLightboxTouchMove}
            onTouchEnd={handleLightboxTouchEnd}
          >
            {/* Close button */}
            <button
              className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 backdrop-blur-md border border-white/10 hover:bg-white/20 active:bg-white/30 flex items-center justify-center transition-colors z-10"
              onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
              aria-label="Close"
            >
              <X className="w-5 h-5 text-white" />
            </button>

            {/* Image counter on mobile */}
            {images.length > 1 && (
              <div className="absolute top-5 left-5 md:hidden text-white/80 text-xs font-body font-medium z-10 bg-white/10 backdrop-blur-md border border-white/10 rounded-full px-3 py-1">
                {selectedImage + 1} / {images.length}
              </div>
            )}

            {/* Nav arrows — hidden on mobile (swipe instead) */}
            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="hidden md:flex absolute left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 backdrop-blur-md border border-white/10 hover:bg-white/20 items-center justify-center transition-colors z-10"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-5 h-5 text-white" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 backdrop-blur-md border border-white/10 hover:bg-white/20 items-center justify-center transition-colors z-10"
                  aria-label="Next"
                >
                  <ChevronRight className="w-5 h-5 text-white" />
                </button>
              </>
            )}

            {/* Main lightbox image with pinch-zoom */}
            <img
              src={currentImage}
              alt={product.name}
              className="max-w-[92vw] max-h-[88vh] object-contain transition-transform duration-150 select-none"
              style={{
                transform: `scale(${lightboxScale}) translate(${lightboxTranslate.x / lightboxScale}px, ${lightboxTranslate.y / lightboxScale}px)`,
              }}
              onClick={(e) => e.stopPropagation()}
              draggable={false}
            />

            {/* Dot indicators on mobile, thumbnails on desktop */}
            {images.length > 1 && (
              <>
                {/* Mobile dots */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex md:hidden gap-2 bg-white/10 backdrop-blur-md border border-white/10 rounded-full px-3 py-2">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      onClick={(e) => { e.stopPropagation(); setSelectedImage(i); }}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
                        selectedImage === i ? "bg-white scale-125" : "bg-white/30"
                      }`}
                    />
                  ))}
                </div>
                {/* Desktop thumbnails */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex gap-2 bg-white/10 backdrop-blur-md border border-white/10 rounded-full px-3 py-2">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      onClick={(e) => { e.stopPropagation(); setSelectedImage(i); }}
                      className={`w-12 h-12 rounded-sm overflow-hidden transition-all ${
                        selectedImage === i ? "ring-2 ring-white" : "opacity-40 hover:opacity-80"
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Swipe hint on mobile — shows briefly */}
            {lightboxScale <= 1 && images.length > 1 && (
              <p className="absolute bottom-14 left-1/2 -translate-x-1/2 md:hidden text-white/40 text-[10px] font-body animate-pulse">
                Swipe to navigate · Pinch to zoom
              </p>
            )}
          </div>
        )}

        {/* Related Products */}
        {filteredRelated.length > 0 && (
          <section className="container py-14 border-t border-border">
            <h2 className="font-display text-xl md:text-2xl font-bold mb-8 text-center">
              You May Also Like
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-8">
              {filteredRelated.map((p, i) => (
                <ShopProductCard
                  key={p.id}
                  product={p}
                  onQuickView={setQuickViewProduct}
                  index={i}
                />
              ))}
            </div>
          </section>
        )}

        <BlogStrip
          relatedToProduct={{ name: product.name, categories: product.categories }}
          heading="From the journal"
          subheading="Styling and care reads that pair with this product."
        />
      </main>

      {/* Sticky bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-xl border-t border-border shadow-[0_-4px_20px_-4px_hsl(var(--foreground)/0.06)] transition-transform duration-300 ${
          showStickyBar ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="container flex items-center gap-4 py-2.5">
          <img
            src={getFirstImage(product.images)}
            alt={`${product.name} – Plymhair`}
            className="w-10 h-10 rounded-sm object-cover shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="font-body text-sm font-medium truncate">{product.name}</div>
            <p className="font-display text-sm font-bold">£{price.toFixed(2)}</p>
          </div>
          {product.in_stock ? (
            <>
              <button
                className="flex items-center gap-2 bg-foreground text-background font-body text-xs font-semibold uppercase tracking-wider px-5 py-2.5 rounded-sm hover:bg-foreground/90 transition-colors disabled:opacity-40 shrink-0"
                disabled={product.type === "variable" && attrOptions.length > 0 && !selectedAttr}
                onClick={handleAdd}
              >
                <GlossyIcon name="cart" size={16} />
                Add
              </button>
              <button
                className="flex items-center gap-2 bg-primary text-primary-foreground font-body text-xs font-semibold uppercase tracking-wider px-5 py-2.5 rounded-sm hover:bg-primary/90 transition-colors disabled:opacity-40 shrink-0"
                disabled={product.type === "variable" && !selectedAttr}
                onClick={handleBuyNow}
              >
                <Zap className="w-3.5 h-3.5" />
                Buy Now
              </button>
            </>
          ) : (
            <div className="shrink-0">
              <OrderOfflineButton productName={product.name} productSku={product.sku} variant="compact" />
            </div>
          )}
        </div>
      </div>

      <QuickViewModal
        product={quickViewProduct}
        open={!!quickViewProduct}
        onOpenChange={(open) => !open && setQuickViewProduct(null)}
      />

      <Footer />
    </div>
  );
};

export default ProductDetail;
